import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaTintSlash, FaArrowDown, FaCalendarAlt } from 'react-icons/fa';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement,
    LineElement, BarElement, ArcElement, Title, Tooltip, Filler, Legend
} from 'chart.js';

import AppLayout from '../../layouts/AppLayout';
import useAuth from '../../hooks/useAuth';
import useActiveCondominio from '../../hooks/useActiveCondominio';
import './styles/analytics.css';

ChartJS.register(
    CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Filler, Legend
);

const ReservatorioAnalyticsPage = () => {
    const { id: reservatorioId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Fallback info if user refreshed the page (loss of state)
    const reservatorioData = location.state?.reservatorio || { nome: 'Reservatório', capacidade_litros: null };

    const { user } = useAuth();
    const { activeCondominio, condominios } = useActiveCondominio();

    const getLocalISODate = (d = new Date()) => {
        const tzOffset = d.getTimezoneOffset() * 60000;
        return new Date(d.getTime() - tzOffset).toISOString().split('T')[0];
    };

    const [periodo, setPeriodo] = useState('30d'); // 'custom', '7d', '30d'
    const [customDate, setCustomDate] = useState(getLocalISODate());
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAnalytics = async () => {
            if (!activeCondominio || !reservatorioId) return;

            setLoading(true);
            try {
                let dataInicio, dataFim;
                const hoje = new Date();
                dataFim = getLocalISODate(hoje);

                if (periodo === 'custom') {
                    dataInicio = customDate;
                    dataFim = customDate;
                } else if (periodo === '7d') {
                    const d7 = new Date();
                    d7.setDate(hoje.getDate() - 7);
                    dataInicio = getLocalISODate(d7);
                } else if (periodo === '30d') {
                    const d30 = new Date();
                    d30.setDate(hoje.getDate() - 30);
                    dataInicio = getLocalISODate(d30);
                }

                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';
                const response = await axios.get(`${apiUrl}/api/reservatorios/${reservatorioId}/analytics`, {
                    headers: { 'x-condominio-id': activeCondominio },
                    params: { dataInicio, dataFim }
                });

                setAnalytics(response.data.dados);
            } catch (err) {
                console.error("Erro ao buscar analytics", err);
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, [activeCondominio, reservatorioId, periodo, customDate]);

    // Data builder format ChartJS
    const chartData = {
        labels: analytics?.registros?.map(reg => {
            if (analytics.isAggregated) {
                // Return generic date
                const date = new Date(reg.timestamp_dia);
                // add timezone offset workaround
                date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
                return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
            } else {
                // Return time
                return new Date(reg.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            }
        }) || [],
        datasets: [
            {
                label: 'Nível Médio (%)',
                data: analytics?.registros?.map(reg =>
                    analytics.isAggregated ? reg.nivel_percentual_media : reg.nivel_percentual
                ) || [],
                fill: true,
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return 'rgba(0, 123, 255, 0.08)';
                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, 'rgba(0, 123, 255, 0.2)');
                    gradient.addColorStop(1, 'rgba(0, 123, 255, 0.0)');
                    return gradient;
                },
                borderColor: '#007bff',
                tension: 0.4,
                pointRadius: analytics?.isAggregated ? 4 : 0,
                pointHoverRadius: 6,
                borderWidth: 2.5
            }
        ]
    };

    // If it's aggregated, we can add a min-max band. Since ChartJS isn't perfect for bands without dual lines, 
    // we will just draw the daily average, and indicate the extremes in the Tooltip.
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: (context) => {
                        let label = context.dataset.label || '';
                        if (label) label += ': ';
                        if (context.parsed.y !== null) {
                            label += context.parsed.y.toFixed(1) + '%';
                        }
                        return label;
                    },
                    afterBody: (tooltipItems) => {
                        if (analytics?.isAggregated) {
                            const index = tooltipItems[0].dataIndex;
                            const reg = analytics.registros[index];
                            return `\nMáxima do dia: ${reg.nivel_percentual_max.toFixed(1)}%\nMínima do dia: ${reg.nivel_percentual_min.toFixed(1)}%`;
                        }
                    }
                }
            }
        },
        scales: {
            y: { min: 0, max: 100 },
            x: {
                grid: { display: false }
            }
        }
    };

    // ----- FASE A.2: Gráficos de Insights -----
    const healthData = {
        labels: ['Abaixo do Mín.', 'Volume Normal', 'Acima do Máx.'],
        datasets: [{
            data: [
                analytics?.healthGauge?.belowMin || 0,
                analytics?.healthGauge?.normal || 0,
                analytics?.healthGauge?.aboveMax || 0
            ],
            backgroundColor: [
                'rgba(239, 68, 68, 0.85)',   // Danger red
                'rgba(59, 130, 246, 0.85)',  // Safe blue
                'rgba(245, 158, 11, 0.85)'   // Warning yellow
            ],
            borderWidth: 0,
            hoverOffset: 4
        }]
    };

    const healthOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
            tooltip: {
                callbacks: {
                    label: (ctx) => ` ${ctx.label}: ${ctx.raw} leituras`
                }
            }
        },
        cutout: '70%'
    };

    const hourlyLabels = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);
    const hourlyRawData = new Array(24).fill(0);
    if (analytics?.hourlyProfile) {
        analytics.hourlyProfile.forEach(h => {
            hourlyRawData[h.hora_do_dia] = h.nivel_percentual_media;
        });
    }

    const hourlyData = {
        labels: hourlyLabels,
        datasets: [{
            label: 'Média de Nível (%) no Horário',
            data: hourlyRawData,
            backgroundColor: 'rgba(59, 130, 246, 0.6)',
            borderColor: 'rgba(59, 130, 246, 1)',
            borderWidth: 1,
            borderRadius: 4,
            barPercentage: 0.8
        }]
    };

    const hourlyOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => ` Média: ${ctx.parsed.y.toFixed(1)}%`
                }
            }
        },
        scales: {
            y: { min: 0, max: 100 },
            x: { grid: { display: false } }
        }
    };

    const getCustomDateLabel = () => {
        const todayStr = getLocalISODate();
        if (customDate === todayStr) return "Hoje (Granular)";
        const d = new Date(customDate);
        d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
        return d.toLocaleDateString('pt-BR') + " (Granular)";
    };

    return (
        <AppLayout
            titulo={`Analytics - ${reservatorioData.nome}`}
            cargo={user?.cargo}
            condominios={condominios}
            activeCondominio={activeCondominio}
            onCondomínioChange={() => { }} // Handle globally
            isChecking={loading}
        >
            <div className="analytics-page-container">
                <div className="analytics-header">
                    <button className="back-btn" onClick={() => navigate('/reservatorios')}>
                        <FaArrowLeft /> Voltar para Monitoramento
                    </button>
                    <h2>Histórico de Desempenho: {reservatorioData.nome}</h2>
                </div>

                <div className="analytics-controls">
                    <div className="filters-group">
                        <button
                            className={`filter-btn custom-date-btn ${periodo === 'custom' ? 'active' : ''}`}
                            onClick={() => setPeriodo('custom')}
                        >
                            <span>{getCustomDateLabel()}</span>
                            <div className="date-picker-icon-box">
                                <FaCalendarAlt />
                                <input
                                    type="date"
                                    className="analytics-date-picker-invisible"
                                    value={customDate}
                                    max={getLocalISODate()}
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            setCustomDate(e.target.value);
                                            setPeriodo('custom');
                                        }
                                    }}
                                />
                            </div>
                        </button>
                        <button className={`filter-btn ${periodo === '7d' ? 'active' : ''}`} onClick={() => setPeriodo('7d')}>Últimos 7 dias</button>
                        <button className={`filter-btn ${periodo === '30d' ? 'active' : ''}`} onClick={() => setPeriodo('30d')}>Últimos 30 dias (Média)</button>
                    </div>
                </div>

                {loading ? (
                    <div className="analytics-loading">Carregando dados matemáticos...</div>
                ) : (
                    <>
                        <div className="analytics-dashboard-grid">

                            <div className="chart-main-card">
                                <div className="chart-wrapper">
                                    {analytics?.registros?.length > 0 ? (
                                        <Line data={chartData} options={chartOptions} />
                                    ) : (
                                        <div className="empty-chart">Sem dados de leitura no período.</div>
                                    )}
                                </div>
                            </div>

                            <div className="highlights-sidebar">
                                <div className="highlight-card danger">
                                    <div className="highlight-icon"><FaTintSlash /></div>
                                    <div className="highlight-content">
                                        <span className="hl-label">Nível Crítico Alcançado</span>
                                        <span className="hl-value">
                                            {analytics?.highlights?.menorNivel ? `${analytics.highlights.menorNivel.valor.toFixed(1)}%` : 'N/A'}
                                        </span>
                                        <span className="hl-sub">
                                            {analytics?.highlights?.menorNivel?.timestamp ? new Date(analytics.highlights.menorNivel.timestamp).toLocaleString('pt-BR') : '-'}
                                        </span>
                                    </div>
                                </div>

                                <div className="highlight-card warning">
                                    <div className="highlight-icon"><FaArrowDown /></div>
                                    <div className="highlight-content">
                                        <span className="hl-label">
                                            {analytics?.isAggregated ? "Maior queda de média (Dia de maior consumo)" : "Maior Queda Registrada (Pico de Uso)"}
                                        </span>
                                        <span className="hl-value">
                                            {analytics?.highlights?.maiorQueda ? `-${analytics.highlights.maiorQueda.valor.toFixed(1)}%` : 'N/A'}
                                        </span>
                                        <span className="hl-sub" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                                            {analytics?.highlights?.maiorQueda?.intervalo ? (
                                                analytics.isAggregated
                                                    ? `Entre ${new Date(analytics.highlights.maiorQueda.intervalo.de).toLocaleDateString('pt-BR')} e ${new Date(analytics.highlights.maiorQueda.intervalo.ate).toLocaleDateString('pt-BR')}`
                                                    : `Entre ${new Date(analytics.highlights.maiorQueda.intervalo.de).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} e ${new Date(analytics.highlights.maiorQueda.intervalo.ate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} (${new Date(analytics.highlights.maiorQueda.intervalo.ate).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })})`
                                            ) : '-'}
                                        </span>
                                    </div>
                                </div>

                                <div className="info-card">
                                    <h4>Comportamento do Período</h4>
                                    <p>
                                        As amostras refletem {analytics?.isAggregated ? "agrupamentos focados nas médias diárias e picos de mínimo" : "todas as oscilações ao vivo detectadas a cada leitura do sistema"}.
                                        A maior queda isolada listada indica o maior fluxo de água consumido em um curto espaço de tempo.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="analytics-insights-section">
                            <div className="insights-grid">
                                <div className="chart-main-card">
                                    <h3 className="section-group-title">Índice de Operação Segura (%)</h3>
                                    <p className="section-subtitle">Distribuição baseada nos limiares: mín {analytics?.healthGauge?.configUtilizada?.nivel_minimo}% e máx {analytics?.healthGauge?.configUtilizada?.nivel_maximo}%</p>
                                    <div className="chart-wrapper pie-wrapper">
                                        <Doughnut data={healthData} options={healthOptions} />
                                        <div className="pie-centertext">
                                            <span>Total</span>
                                            <strong>{analytics?.healthGauge?.total}</strong>
                                            <span>amostras</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="chart-main-card">
                                    <h3 className="section-group-title">Perfil Constante de Consumo (Horas)</h3>
                                    <p className="section-subtitle">Qual hora do dia o nível costuma ser mais baixo?</p>
                                    <div className="chart-wrapper">
                                        {analytics?.hourlyProfile?.length > 0 ? (
                                            <Bar data={hourlyData} options={hourlyOptions} />
                                        ) : (
                                            <div className="empty-chart">Sem perfil detectável</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="chart-main-card table-wrapper-card">
                                <div className="table-header">
                                    <h3 className="section-group-title">Registro Auditável do Período Selecionado</h3>
                                    <span className="table-badge">{analytics?.isAggregated ? 'Consolidação Diária' : 'Visão Sensorial (Granular)'}</span>
                                </div>
                                <div className="table-scroll-container">
                                    <table className="medicoes-table">
                                        <thead>
                                            <tr>
                                                <th>Data/Hora da Aferição</th>
                                                {analytics?.isAggregated ? (
                                                    <>
                                                        <th>Média do Dia</th>
                                                        <th>Pico Máx. (Menos Consumo)</th>
                                                        <th>Pico Mín. (Mais Consumo)</th>
                                                        <th>Qtd. Leituras de Sensor</th>
                                                    </>
                                                ) : (
                                                    <>
                                                        <th>Nível Registrado</th>
                                                        <th>Qtd. Leituras</th>
                                                    </>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {analytics?.registros?.slice().reverse().map((reg, idx) => {
                                                const minL = analytics?.healthGauge?.configUtilizada?.nivel_minimo || 20;
                                                const maxL = analytics?.healthGauge?.configUtilizada?.nivel_maximo || 90;
                                                return (
                                                    <tr key={idx}>
                                                        {analytics.isAggregated ? (
                                                            <>
                                                                <td>
                                                                    {(() => {
                                                                        const dt = new Date(reg.timestamp_dia);
                                                                        dt.setMinutes(dt.getMinutes() + dt.getTimezoneOffset());
                                                                        return dt.toLocaleDateString('pt-BR');
                                                                    })()}
                                                                </td>
                                                                <td>
                                                                    <span className={`medicao-badge ${reg.nivel_percentual_media < minL ? 'medicao-badge--danger' : reg.nivel_percentual_media > maxL ? 'medicao-badge--warning' : 'medicao-badge--normal'}`}>
                                                                        {reg.nivel_percentual_media.toFixed(1)}%
                                                                    </span>
                                                                </td>
                                                                <td>{reg.nivel_percentual_max.toFixed(1)}%</td>
                                                                <td>{reg.nivel_percentual_min.toFixed(1)}%</td>
                                                                <td>{reg.leituras}</td>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <td>{new Date(reg.timestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                                                                <td>
                                                                    <span className={`medicao-badge ${reg.nivel_percentual < minL ? 'medicao-badge--danger' : reg.nivel_percentual > maxL ? 'medicao-badge--warning' : 'medicao-badge--normal'}`}>
                                                                        {reg.nivel_percentual.toFixed(1)}%
                                                                    </span>
                                                                </td>
                                                                <td>1</td>
                                                            </>
                                                        )}
                                                    </tr>
                                                );
                                            })}
                                            {analytics?.registros?.length === 0 && (
                                                <tr>
                                                    <td colSpan="5" style={{ textAlign: 'center', padding: '30px', color: 'var(--color-text-tertiary)' }}>Sem amostras registradas para esta janela de tempo.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </AppLayout>
    );
};

export default ReservatorioAnalyticsPage;
